import * as actionTypes from './actionTypes';
import * as ajaxEndpoints from '../../api/ajax-endpoints';
import { getDataWithDotNet, postDataWithDotNet } from '../../newApis/dotNetApiMethods';
import { appInsights } from '../../config/appInsights';

// Synchronous actions
export const updateFormData = (formData: any) => {
  return {
    type: actionTypes.INVESTMENT_FORM_UPDATE_DATA,
    payload: formData,
  };
};

export const setWalletBalance = (balance: number) => {
  return {
    type: actionTypes.INVESTMENT_FORM_SET_WALLET_BALANCE,
    payload: balance,
  };
};

export const setCurrentStep = (step: number) => {
  return {
    type: actionTypes.INVESTMENT_FORM_SET_CURRENT_STEP,
    payload: step,
  };
};

export const resetForm = () => {
  return {
    type: actionTypes.INVESTMENT_FORM_RESET,
  };
};

export const setCalculatedInterest = (data: any) => {
  return {
    type: actionTypes.INVESTMENT_FORM_SET_CALCULATED_INTEREST,
    payload: data,
  };
};

export const calculateInterestStart = () => {
  return {
    type: actionTypes.INVESTMENT_FORM_CALCULATE_INTEREST_START,
  };
};

export const calculateInterestSuccess = (data: any) => {
  return {
    type: actionTypes.INVESTMENT_FORM_CALCULATE_INTEREST_SUCCESS,
    payload: data,
  };
};

export const calculateInterestFailure = (error: any) => {
  return {
    type: actionTypes.INVESTMENT_FORM_CALCULATE_INTEREST_FAILURE,
    payload: error,
  };
};

export const toggleBreakdownModal = (show?: boolean) => {
  return {
    type: actionTypes.INVESTMENT_FORM_TOGGLE_BREAKDOWN_MODAL,
    payload: show,
  };
};

export const setTransactionReceipt = (receipt: { base64: string; fileName: string }) => {
  return {
    type: actionTypes.INVESTMENT_FORM_SET_TRANSACTION_RECEIPT,
    payload: receipt,
  };
};

export const setSelectedInvestor = (investor: any) => {
  return {
    type: actionTypes.INVESTMENT_FORM_SET_SELECTED_INVESTOR,
    payload: investor,
  };
};

// Asynchronous actions
export const fetchWalletBalanceStart = () => {
  return {
    type: actionTypes.INVESTMENT_FORM_FETCH_WALLET_BALANCE_START,
  };
};

export const fetchWalletBalanceSuccess = (data: any) => {
  return {
    type: actionTypes.INVESTMENT_FORM_FETCH_WALLET_BALANCE_SUCCESS,
    payload: data,
  };
};

export const fetchWalletBalanceFailure = (error: any) => {
  return {
    type: actionTypes.INVESTMENT_FORM_FETCH_WALLET_BALANCE_FAILURE,
    payload: error,
  };
};

// Action to set available wallets
export const setAvailableWallets = (wallets: any[]) => {
  return {
    type: actionTypes.INVESTMENT_FORM_SET_AVAILABLE_WALLETS,
    payload: wallets,
  };
};

// Thunk for fetching wallet balance
export const fetchWalletBalance = (
  investmentProductDetailId: number,
  funderId: number,
  currencyCode: string
) => {
  return async (dispatch: any) => {
    dispatch(fetchWalletBalanceStart());

    try {
      // First, get the list of customer wallets
      const walletsResponse = await getDataWithDotNet(
        `${ajaxEndpoints.GET_CUSTOMER_WALLETS}?customerId=${funderId}&investmentProductDetailId=${investmentProductDetailId}`
      );
      
      // Store the wallets in the Redux store
      if (walletsResponse.data && walletsResponse.data.wallets) {
        dispatch(setAvailableWallets(walletsResponse.data.wallets));
      }
      
      // Then get the wallet balance as before
      const response = await getDataWithDotNet(
        `${ajaxEndpoints.GET_CUSTOMER_WALLET_BY_INVESTMENT_PRODUCT}?investmentProductId=${investmentProductDetailId}&funderId=${funderId}&currencyCode=${currencyCode}`
      );
      
      dispatch(fetchWalletBalanceSuccess(response.data));
      return response.data;
    } catch (error) {
      appInsights.trackException({
        exception: error as Error,
        properties: {
          fileName: 'investmentForm.ts',
        },
      });
      dispatch(fetchWalletBalanceFailure(error));
      return null;
    }
  };
};

// Thunk for calculating interest
export const calculateInterest = () => {
  return async (dispatch: any, getState: any) => {
    dispatch(calculateInterestStart());
    
    try {
      const state = getState();
      const { formData } = state.investmentFormReducer;
      
      // Check for required values
      if (!formData.tenure || !formData.interest_repayment_type || !formData.principal_amount) {
        return;
      }
      
      // API call to calculate interest
      const response = await postDataWithDotNet(
        ajaxEndpoints.CALCULATE_INVESTMENT_INTEREST,
        {
          interest_rate: formData.interest_rate,
          principal_amount: Number(formData.principal_amount),
          tenure: Number(formData.tenure),
          start_date: formData.effective_date || new Date().toISOString(),
          interest_repayment_type: formData.interest_repayment_type,
          number_of_days_in_year: formData.number_of_days_in_year || 365,
        }
      );
      
      const result = {
        interest_calculated: response.data.interest_amount,
        maturity_amount: response.data.maturity_amount,
        monthly_interest_payable: response.data.monthly_interest_payable,
        monthly_interest_accruals: response.data.monthly_interest_accruals,
      };
      
      dispatch(calculateInterestSuccess(result));
      return result;
    } catch (error) {
      appInsights.trackException({
        exception: error as Error,
        properties: {
          fileName: 'investmentForm.ts',
        },
      });
      dispatch(calculateInterestFailure(error));
      return null;
    }
  };
};